/**
 * La guía de referencia de un flujo: para qué está, qué lo inicia, su alcance y
 * sus recorridos.
 *
 * Se muestra en dos lugares y por eso vive una sola vez: en la página de
 * Procesos y en el panel lateral que se abre desde una corrida o un evento. Si
 * cada uno la dibujara por su cuenta, tarde o temprano dirían cosas distintas.
 *
 * - `full`: la de Procesos. Trae su título (con el enlace a los mensajes del
 *   flujo), un panel por bloque y los pasos de cada recorrido en fila.
 * - `compact`: para un panel angosto. Sin título propio —lo pone quien la
 *   contiene— y con los pasos apilados en vertical, como una línea de tiempo.
 */
import { useId } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type { ProcessPath, ProcessReference } from '../lib/process-reference';
import '../styles/process-reference.css';

export type ProcessReferenceVariant = 'full' | 'compact';

export interface ProcessReferenceContentProps {
  readonly process: ProcessReference;
  readonly variant?: ProcessReferenceVariant | undefined;
  /** Id del título (variante `full`), para el `aria-labelledby` de quien la contiene. */
  readonly titleId?: string | undefined;
}

export function ProcessReferenceContent({
  process,
  variant = 'full',
  titleId,
}: ProcessReferenceContentProps): ReactElement {
  const generatedTitleId = useId();

  if (variant === 'compact') {
    return (
      <div className="process-reference">
        <p className="process-reference__purpose">{process.purpose}</p>
        <ProcessFacts process={process} />
        {process.paths.map((path) => (
          <section className="process-reference__path" key={path.title}>
            <h3 className="process-reference__path-title">{path.title}</h3>
            <ProcessSteps path={path} stacked />
          </section>
        ))}
      </div>
    );
  }

  return (
    <>
      <section className="panel">
        <header className="panel__header process-heading">
          <h2 id={titleId ?? generatedTitleId}>
            {process.id} · {process.title}
          </h2>
          <Link className="button button--sm" to={`/mensajes?flow=${process.id}`}>
            Ver mensajes de {process.id}
          </Link>
        </header>
        <div className="panel__body">
          <p>{process.purpose}</p>
          <ProcessFacts process={process} />
        </div>
      </section>
      {process.paths.map((path) => (
        <section className="panel" key={path.title}>
          <header className="panel__header">
            <h3>{path.title}</h3>
          </header>
          <div className="panel__body">
            <ProcessSteps path={path} stacked={false} />
          </div>
        </section>
      ))}
    </>
  );
}

function ProcessFacts({ process }: { readonly process: ProcessReference }): ReactElement {
  return (
    <dl className="process-facts">
      <div>
        <dt>Qué lo inicia</dt>
        <dd>{process.trigger}</dd>
      </div>
      <div>
        <dt>Alcance y dependencias</dt>
        <dd>{process.scope}</dd>
      </div>
    </dl>
  );
}

/**
 * Los pasos de un recorrido. El marcado es el mismo en las dos variantes; cambia
 * el bloque de clases: `process-diagram` (en fila, global.css) o `process-steps`
 * (apilado, process-reference.css).
 */
function ProcessSteps({
  path,
  stacked,
}: {
  readonly path: ProcessPath;
  readonly stacked: boolean;
}): ReactElement {
  const block = stacked ? 'process-steps' : 'process-diagram';
  return (
    <ol className={block} aria-label={path.title}>
      {path.steps.map((step, index) => (
        <li className={`${block}__step`} key={step.action}>
          <span className={`${block}__number`} aria-hidden="true">
            {index + 1}
          </span>
          <span className={`${block}__system`}>{step.system}</span>
          <strong>{step.action}</strong>
          <p>{step.explanation}</p>
        </li>
      ))}
    </ol>
  );
}
